import {
  Check,
  ChevronDown,
  ChevronsUpDown,
  LogOut,
  Mail,
  Plus,
  Settings,
  Sparkles,
  UserRound,
} from "lucide-react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { useLogout, useSession, useSwitchOrganisation } from "@/shared/auth/session";
import { describeError } from "@/shared/api/errors";
import { ROUTES } from "@/shared/constants/routes";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { CURRENT_WEB_VERSION } from "@/shared/releases/releases";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { cn } from "@/shared/ui/cn";
import { Icon } from "@/shared/ui/icon";
import { pluralize } from "@/shared/utils/labels";

interface UserMenuProps {
  /**
   * `bar`: the 28 px bordered pill pass 01 drew for the top bar. `sidebar`: the full-width account
   * row at the foot of the sidebar the shell refresh (2026-09-22) moved it to — avatar, the person's
   * name over the organisation and role, and a chevron — which collapses to the avatar alone on the
   * rail. Same menu behind both; only the trigger differs.
   */
  variant?: "bar" | "sidebar";
  /** Sidebar variant only: the rail is open, so draw the avatar and nothing else. */
  collapsed?: boolean;
}

export function UserMenu({ variant = "bar", collapsed = false }: UserMenuProps) {
  const { user, tenant, role, pendingInvites, memberships } = useSession();
  const switchOrg = useSwitchOrganisation();
  const switchable = memberships.filter((m) => m.status === "active");
  const selectOrg = (membershipId: string) => {
    if (membershipId === memberships.find((m) => m.tenantId === tenant.id)?.membershipId) return;
    switchOrg.mutate(
      { membershipId },
      {
        onSuccess: (result) => {
          toast.success(`Switched to ${result.tenant?.name ?? "organisation"}`);
          // Back to the overview: the page you were on may not exist for the organisation you just
          // moved to, and a 404 is a worse welcome than a dashboard.
          void navigate(ROUTES.overview, { replace: true });
        },
        onError: (error) => {
          const { title, detail } = describeError(error);
          toast.error(title, detail ? { description: detail } : undefined);
        },
      },
    );
  };
  const { can } = usePermissions();
  const navigate = useNavigate();
  const logout = useLogout(() => void navigate(ROUTES.login, { replace: true }));
  const display = user.name || user.mobile;
  const initial = user.name ? user.name.slice(0, 1).toUpperCase() : null;
  const label = `Account and organisation menu for ${display}`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "sidebar" ? (
          /*
            The account row: who is signed in, over which organisation as what. It is the one place
            the sidebar names the organisation in words, so the reader who opens the drawer on a
            phone or glances at the rail's foot on a desk has the same answer without opening anything.
          */
          <button
            type="button"
            aria-label={label}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-md text-left transition-colors duration-120 hover:bg-surface",
              collapsed ? "h-10 justify-center px-0" : "h-12 px-2",
            )}
          >
            {/* Soft accent, not solid: solid `--accent` is the primary button, the focus ring and the
                current nav entry (tokens.css) — an avatar is none of those. */}
            <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-accent-soft text-cell font-semibold text-accent-fg">
              {initial ?? <Icon icon={UserRound} size={16} />}
            </span>
            {!collapsed ? (
              <>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-cell font-medium text-fg">{display}</span>
                  <span className="truncate text-caption text-fg-muted">
                    <span>{tenant.name}</span>
                    {role.name ? ` · ${role.name}` : ""}
                  </span>
                </span>
                <Icon icon={ChevronsUpDown} size={16} className="size-3.5 shrink-0 text-fg-faint" />
              </>
            ) : null}
          </button>
        ) : (
          /*
            Board `1a`: ONE control on the right that is both the account and the organisation — a
            28 px bordered pill holding a 20 px avatar, the organisation's name at 400, and a chevron.
          */
          <button
            type="button"
            aria-label={label}
            className="flex h-7 max-w-56 items-center gap-1.5 rounded-sm border border-border py-0 pl-[3px] pr-1.5 transition-colors duration-120 hover:bg-surface-hover"
          >
            <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-sm bg-accent-soft font-semibold text-accent-fg [font-size:11px] [line-height:1]">
              {initial ?? <Icon icon={UserRound} size={16} className="size-3.5" />}
            </span>
            <span className="hidden truncate text-cell md:inline">{tenant.name}</span>
            <Icon icon={ChevronDown} size={16} className="size-3.5 shrink-0 text-fg-faint" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === "sidebar" ? "start" : "end"}
        {...(variant === "sidebar" ? { side: "top" as const } : {})}
        className="min-w-56"
      >
        <DropdownMenuLabel>
          <span className="block truncate text-cell font-medium text-fg">{display}</span>
          <span className="block truncate">
            {tenant.name} · {role.name}
          </span>
          <span className="block truncate font-mono">{tenant.timezone}</span>
        </DropdownMenuLabel>
        {/*
          Organisation switching lives HERE since the 2026-09 redesign. Board `1a` draws one control
          on the right that is both the account and the organisation, so the separate switcher that
          used to sit beside it is gone — but switching is not: it is the first thing this menu
          offers, because "which organisation am I in" is the question the control's label answers.
        */}
        {switchable.length > 1 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Your organisations</DropdownMenuLabel>
            {switchable.map((m) => (
              <DropdownMenuItem
                key={m.membershipId}
                onSelect={() => selectOrg(m.membershipId)}
                aria-current={m.tenantId === tenant.id}
              >
                <span className="inline-flex size-4 items-center justify-center">
                  {m.tenantId === tenant.id ? <Icon icon={Check} size={16} /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-fg">{m.tenantName}</span>
                  <span className="block truncate text-caption text-fg-muted">{m.roleName}</span>
                </span>
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={ROUTES.onboardingCreateOrganisation} className="text-fg no-underline">
            <span className="inline-flex size-4 items-center justify-center">
              <Icon icon={Plus} size={16} />
            </span>
            Create organisation
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {pendingInvites.length > 0 ? (
          <DropdownMenuItem asChild>
            <Link to={ROUTES.onboardingInvitations} className="text-fg no-underline">
              <Icon icon={Mail} size={16} />
              {pluralize(pendingInvites.length, "invitation")} waiting
            </Link>
          </DropdownMenuItem>
        ) : null}
        {can("settings", "view") ? (
          <DropdownMenuItem asChild>
            <Link to={ROUTES.settings} className="text-fg no-underline">
              <Icon icon={Settings} size={16} />
              Organisation settings
            </Link>
          </DropdownMenuItem>
        ) : null}
        {/* The version the portal is on, and the way to everything that changed in it (public page, no sign-in). */}
        <DropdownMenuItem asChild>
          <Link to={ROUTES.whatsNew} className="text-fg no-underline">
            <Icon icon={Sparkles} size={16} />
            What&apos;s new
            <span className="ml-auto pl-3 text-caption text-fg-muted">Version {CURRENT_WEB_VERSION}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem tone="danger" onSelect={() => logout.mutate()} disabled={logout.isPending}>
          <Icon icon={LogOut} size={16} />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
